import {
  CollaborativeProvider,
  useCollaborative
} from "pages/TopicViewer/hooks/use-collaborative";
import { EditorContent, useEditor } from "@tiptap/react";
import { Markdown } from "@tiptap/markdown";
import Collaboration from "@tiptap/extension-collaboration";
import CollaborationCaret from "@tiptap/extension-collaboration-caret";
import StarterKit from "@tiptap/starter-kit";
import { useMemo } from "react";
import { useAccount } from "@vojomo-core/shared";
import { getColorForUserName } from "utils/helpers/getColorForUserName";
import Placeholder from "@tiptap/extension-placeholder";
import { Table, TableRow } from "@tiptap/extension-table";
import { ReadOnlyFirstColumnCell } from "components/TipTapEditorPlugins/ReadOnlyFirstColumnCell";
import { ReadOnlyTableHeader } from "components/TipTapEditorPlugins/ReadyOnlyTableHeader";
import { DisableDragDrop } from "components/TipTapEditorPlugins/DisableDragDrop";
import { ReadOnlyEditor } from "components/ReadyOnlyEditor";
import Image from "@tiptap/extension-image";

export const CollaborativeEditorInternal = ({ isEditable, offlineText }) => {
  const { provider, ydoc, isSynced } = useCollaborative();
  const { vojomoAccount } = useAccount();

  const editorConfig = useMemo(() => {
    if (!isSynced) {
      return {
        extensions: [
          Image,
          StarterKit,
          Table,
          TableRow,
          ReadOnlyTableHeader,
          ReadOnlyFirstColumnCell,
          Markdown
        ],
        editable: false,
        contentType: "markdown" as const,
        content: offlineText
      };
    } else {
      return {
        extensions: [
          StarterKit.configure({ undoRedo: false }),
          Table,
          Image,
          TableRow,
          ReadOnlyTableHeader,
          DisableDragDrop,
          ReadOnlyFirstColumnCell,
          Markdown,
          Collaboration.configure({
            fragment: ydoc.getXmlFragment("prosemirror")
          }),
          ...(provider?.awareness
            ? [
                CollaborationCaret.configure({
                  provider: provider,
                  user: {
                    name: vojomoAccount.name,
                    id: vojomoAccount["_id"],
                    color: getColorForUserName(vojomoAccount.name)
                  },
                  render: (user) => {
                    const cursor = document.createElement("span");
                    cursor.classList.add("collab-caret");
                    cursor.style.borderLeft = `2px solid ${user.color}`;
                    cursor.style.marginLeft = "-1px";
                    cursor.style.height = "1em";
                    cursor.style.position = "relative";

                    const label = document.createElement("div");
                    label.className = "collab-label";
                    label.textContent = user.name || "Anonymous";
                    label.style.position = "absolute";
                    label.style.top = "-1.2em";
                    label.style.left = "0";
                    label.style.backgroundColor = user.color;
                    label.style.color = "white";
                    label.style.padding = "0 4px";
                    label.style.fontSize = "0.65em";
                    label.style.borderRadius = "4px";
                    label.style.whiteSpace = "nowrap";

                    cursor.appendChild(label);
                    return cursor;
                  }
                })
              ]
            : []),
          Placeholder.configure({
            placeholder: "Start typing here...",
            showOnlyWhenEditable: true
          })
        ],
        editable: isEditable
      };
    }
  }, [isEditable, provider, isSynced]);

  const editor = useEditor(editorConfig, [editorConfig]);

  if (!isSynced) {
    return <ReadOnlyEditor content={offlineText} />;
  }

  return <EditorContent editor={editor} />;
};

export const CollaborativeEditor = ({ roomName, isEditable, offlineText }) => {
  return (
    <CollaborativeProvider roomName={roomName} isDisabled={false}>
      <CollaborativeEditorInternal
        isEditable={isEditable}
        offlineText={offlineText}
      />
    </CollaborativeProvider>
  );
};
